import { formatDate } from '@common/paymentsUtils/dateUtils';
import { IconEditFilled } from '@zillow/constellation-icons';
import { Box, Icon, Paragraph, Text } from '@zillow/constellation-10';
import TransactionConstants from '@common/constants/TransactionConstants';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { HiddenText } from '@common/components/HiddenText/HiddenText';
import { RenterPaymentMethodInterface, RenterPaymentSubscriptionInterface } from '@renter-hub/models/Types';
import PontoonConstants from '@common/constants/PontoonExceptionConstants';
import PaymentMethodModule from '@renter-hub/components/PaymentMethodModule';
import { RENT_PAYMENT } from '@renter-hub/constants/paymentConstants';

interface NextPayableInterface {
  isScheduled: boolean;
}
interface Props {
  currentTransaction?: any;
  paymentSubscriptionDetails?: RenterPaymentSubscriptionInterface;
  accountType?: any;
  goToEditPaymentSchedulingPage?: any;
  goToEditPaymentFrequencyPage?: any;
  goToEditPaymentInfoPage?: (typeOfPaymentToMake?: typeof RENT_PAYMENT) => void;
  displayHiddenText?: boolean;
  allowAutoPayEdit?: boolean;
  paymentMethods: RenterPaymentMethodInterface[];
  nextPayable: NextPayableInterface;
}

export const PaymentDetails = (props: Props) => {
  const {
    currentTransaction = { amount: 0 },
    paymentSubscriptionDetails = {
      nextScheduledDate: '',
      recurring: null,
      paymentMethodId: '',
    },
    accountType,
    goToEditPaymentSchedulingPage,
    goToEditPaymentFrequencyPage,
    displayHiddenText,
    allowAutoPayEdit,
    paymentMethods,
    goToEditPaymentInfoPage,
    nextPayable,
  } = props;

  const { dueDate, amount, zgFee, totalAmount, dateCreated, renterStatus, accountName, accountLast4 } =
    currentTransaction;
  const { nextScheduledDate, recurring, paymentMethodId } = paymentSubscriptionDetails;
  const sendDateExists = dateCreated || nextScheduledDate;
  const sendDate = formatDate(dateCreated || nextScheduledDate, 'do');
  const renterCanEditPayment =
    renterStatus === TransactionConstants.STATUS.UNPAID || renterStatus === TransactionConstants.STATUS.REFUNDED;
  const selectedPaymentMethod = paymentMethods?.find((paymentMethod) => paymentMethod.id === paymentMethodId);
  const isCompleted = renterStatus === TransactionConstants.STATUS.COMPLETE;
  const displayAccountName = isCompleted
    ? accountName
    : selectedPaymentMethod?.displayName || selectedPaymentMethod?.nickname;
  const displayAccountLast4 = isCompleted ? accountLast4 : selectedPaymentMethod?.accountLast4;
  const shouldShowTotalPayment =
    (nextPayable.isScheduled && selectedPaymentMethod && selectedPaymentMethod.accountType !== 'bank_account') ||
    isCompleted;

  const isUsingSelectedPaymentMethod = Boolean(selectedPaymentMethod);

  const displayTotalAmount = isUsingSelectedPaymentMethod
    ? selectedPaymentMethod?.rentAmountDetails?.totalAmount
    : totalAmount;

  const displayFee = isUsingSelectedPaymentMethod ? selectedPaymentMethod?.rentAmountDetails?.fee : zgFee;

  const totalPaymentText = displayTotalAmount
    ? `${centsToDollars(displayTotalAmount)}${
        displayFee && displayFee > 0 ? ` (${centsToDollars(displayFee)} fee)` : ''
      }`
    : null;

  return (
    <>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
        }}
      >
        <Box
          css={{
            paddingRight: 'token(spacing.sm) !important',
            paddingTop: 'token(spacing.sm) !important',
            paddingBottom: 'token(spacing.sm) !important',
          }}
        >
          <Paragraph textStyle="body-bold">Monthly rent</Paragraph>
          <Text>
            {amount ? (
              `${centsToDollars(amount, { style: 'effectiveShort' })}/mo`
            ) : (
              <HiddenText visible={displayHiddenText} />
            )}
          </Text>
        </Box>
        <Box
          css={{
            paddingRight: 'token(spacing.sm) !important',
            paddingTop: 'token(spacing.sm) !important',
            paddingBottom: 'token(spacing.sm) !important',
          }}
        >
          <Paragraph textStyle="body-bold">Due date</Paragraph>
          <Text>
            {dueDate ? (
              `${formatDate(
                dueDate,
                'do',
                PontoonConstants.RENTER_HUB.RENTER_DASHBOARD.RENT_PAYMENT.DUE_DATE,
              )} of the month`
            ) : (
              <HiddenText visible={displayHiddenText} />
            )}
          </Text>
        </Box>
        {renterCanEditPayment && sendDateExists && (
          <Box
            css={{
              paddingRight: 'token(spacing.sm) !important',
              paddingTop: 'token(spacing.sm) !important',
              paddingBottom: 'token(spacing.sm) !important',
            }}
          >
            <Paragraph textStyle="body-bold">
              Send date
              {renterCanEditPayment && (
                <Icon
                  size="sm"
                  css={{ color: 'brand', marginLeft: 'token(spacing.xs) !important' }}
                  onClick={goToEditPaymentSchedulingPage}
                  render={<IconEditFilled />}
                />
              )}
            </Paragraph>
            <Text>{dueDate ? `${sendDate} of the month` : <HiddenText visible={displayHiddenText} />}</Text>
          </Box>
        )}
        <Box
          css={{
            paddingRight: 'token(spacing.sm) !important',
            paddingTop: 'token(spacing.sm) !important',
            paddingBottom: 'token(spacing.sm) !important',
          }}
        >
          <Paragraph textStyle="body-bold">
            Autopay
            {(renterCanEditPayment || allowAutoPayEdit) && (
              <Icon
                size="sm"
                css={{ color: 'brand', marginLeft: 'token(spacing.xs) !important' }}
                onClick={goToEditPaymentFrequencyPage}
                render={<IconEditFilled />}
              />
            )}
          </Paragraph>
          <Text>{!displayHiddenText ? `${recurring ? 'ON' : 'OFF'}` : <HiddenText visible={displayHiddenText} />}</Text>
        </Box>
        {(selectedPaymentMethod || isCompleted) && (
          <Box
            css={{
              paddingRight: 'token(spacing.sm) !important',
              paddingTop: 'token(spacing.sm) !important',
              paddingBottom: 'token(spacing.sm) !important',
            }}
          >
            <PaymentMethodModule
              accountName={displayAccountName}
              accountLast4={displayAccountLast4}
              onClick={() => goToEditPaymentInfoPage && goToEditPaymentInfoPage(RENT_PAYMENT)}
              editable={renterCanEditPayment}
            />
          </Box>
        )}
        {shouldShowTotalPayment && (
          <Box
            css={{
              paddingRight: 'token(spacing.sm) !important',
              paddingTop: 'token(spacing.sm) !important',
              paddingBottom: 'token(spacing.sm) !important',
            }}
          >
            <Paragraph textStyle="body-bold">Total payment</Paragraph>
            <Text>{totalPaymentText ? totalPaymentText : <HiddenText visible={displayHiddenText} />}</Text>
          </Box>
        )}
      </Box>
    </>
  );
};
